// #63 — File d'arbitrage : affaires actives sans `gescomProjectId`.
//
// LECTURE SEULE. Ces affaires bloquent l'export Gescom (lignes « bloquées »).
// Tant que le CSV des affaires n'est pas livré par Gescom (contrat provisoire),
// cet écran est la seule vue sur ce qu'il faudra rattacher ; ensuite, il liste
// ce que le batch `gescom.affaires` n'a pas su rapprocher (sans code CH,
// ambigu…). Le rattachement lui-même n'est pas éditable ici (point ouvert).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Unlink, RefreshCw } from 'lucide-react';
import { apiAdminGescomAffaires } from '../api/client.js';

const card = {
  background: 'var(--card-bg)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-md, 12px)',
  padding: 16,
};

const pill = (active) => ({
  borderRadius: 999,
  padding: '6px 14px',
  border: '1px solid var(--glass-border)',
  background: active ? 'var(--primary)' : 'var(--glass)',
  color: active ? 'var(--card-bg)' : 'var(--text)',
  cursor: 'pointer',
  fontSize: 13,
});

const DAY_OPTIONS = [30, 90, 180];

export default function GescomArbitrageView() {
  const [days, setDays] = useState(90);
  const [onlyActive, setOnlyActive] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiAdminGescomAffaires.arbitrage(days));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(() => {
    const all = data?.items ?? [];
    return onlyActive ? all.filter((a) => a.recent_logged_times > 0) : all;
  }, [data, onlyActive]);

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: 'var(--bg)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          paddingBottom: 12,
          marginBottom: 12,
        }}
      >
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
          <Unlink size={22} /> Arbitrage Gescom
        </h1>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          {DAY_OPTIONS.map((d) => (
            <button key={d} type="button" style={pill(days === d)} onClick={() => setDays(d)} title={`Saisies des ${d} derniers jours`}>
              {d} j
            </button>
          ))}
          <button
            type="button"
            style={pill(onlyActive)}
            onClick={() => setOnlyActive((v) => !v)}
            title="N'afficher que les affaires ayant des saisies sur la période"
          >
            Avec saisies
          </button>
          <button type="button" style={pill(false)} onClick={load} title="Rafraîchir">
            <RefreshCw size={14} style={{ verticalAlign: 'middle' }} />
          </button>
        </div>
      </div>

      <div style={{ ...card, marginBottom: 16, color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.6 }}>
        Affaires actives sans projet Gescom rattaché : leurs saisies ne peuvent pas être exportées vers
        Gescom. {data && (
          <strong style={{ color: 'var(--text)' }}>
            {data.total} affaire(s), dont {data.with_recent_activity} avec des saisies sur {data.days} jours.
          </strong>
        )}
      </div>

      {loading && <div>Chargement…</div>}
      {error && <div style={{ color: 'var(--danger)' }}>Erreur : {error}</div>}

      {!loading && !error && (
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
                <th style={{ padding: '10px 14px' }}>Affaire</th>
                <th style={{ padding: '10px 14px' }}>Client</th>
                <th style={{ padding: '10px 14px' }}>Code CH</th>
                <th style={{ padding: '10px 14px', textAlign: 'right' }}>Saisies</th>
                <th style={{ padding: '10px 14px', textAlign: 'right' }}>Heures</th>
                <th style={{ padding: '10px 14px' }}>Dernière saisie</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 16, color: 'var(--text-muted)' }}>
                    Aucune affaire à arbitrer.
                  </td>
                </tr>
              )}
              {items.map((a) => (
                <tr key={a.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                  <td style={{ padding: '10px 14px' }}>{a.name}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{a.client_name}</td>
                  <td style={{ padding: '10px 14px' }}>
                    {a.ch_code ?? <span style={{ color: 'var(--warning)' }}>sans code</span>}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>{a.recent_logged_times}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>{a.recent_hours.toFixed(1)}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{a.last_logged_at ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
